/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import React from "react";
import { FilePlus, FolderPlus } from "lucide-react";

import { Button } from "@acme/ui/button";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@acme/ui/resizable";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@acme/ui/tooltip";

import { Files } from "~/app/(studio)/studio/_components/panels/files";
import { Finder } from "~/app/(studio)/studio/_components/panels/finder";
import { SectionPanel } from "~/app/(studio)/studio/_components/panels/section-panel";
import { useStudioStore } from "~/app/(studio)/studio/_hooks/use-studio-store";
import { UnauthenticatedState } from "../unauthenticated-state";

interface LeftPanelProps {
  showRemoteRun: boolean;
  authStatus: "authenticated" | "loading" | "unauthenticated";
}

export function LeftPanel({ showRemoteRun, authStatus }: LeftPanelProps) {
  const SHOW_TREE_VIEW = false;

  const leftPanelCollapsed = useStudioStore(
    (state) => state.leftPanelCollapsed,
  );
  const setLeftPanelCollapsed = useStudioStore(
    (state) => state.setLeftPanelCollapsed,
  );

  if (!leftPanelCollapsed) {
    return (
      <>
        <ResizablePanel
          collapsible
          onCollapse={() => {
            setLeftPanelCollapsed(true);
          }}
          defaultSize={20}
          minSize={10}
          maxSize={20}
          id="left-panel"
          order={1}
          className="group/left-panel @container/left-panel"
        >
          {authStatus === "authenticated" ? (
            <TooltipProvider>
              <ResizablePanelGroup direction="vertical" className="h-full">
                {SHOW_TREE_VIEW && (
                  <ResizablePanel
                    defaultSize={showRemoteRun ? 60 : 100}
                    minSize={10}
                    id="tree-view-panel"
                    order={1}
                  >
                    <SectionPanel
                      title="Workspace"
                      headerActions={
                        <div className="flex gap-0.5 opacity-0 transition-opacity group-hover/left-panel:opacity-100">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="iconXs"
                                className="size-6"
                              >
                                <FilePlus className="text-muted-foreground" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" align="center">
                              <span className="font-bold">New file...</span>
                            </TooltipContent>
                          </Tooltip>

                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="iconXs"
                                className="size-6"
                              >
                                <FolderPlus className="text-muted-foreground" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" align="center">
                              <span className="font-bold">New Folder...</span>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      }
                    >
                      <Files />
                    </SectionPanel>
                  </ResizablePanel>
                )}

                {SHOW_TREE_VIEW && showRemoteRun && <ResizableHandle />}

                {showRemoteRun && (
                  <ResizablePanel
                    defaultSize={SHOW_TREE_VIEW ? 40 : 100}
                    collapsedSize={35}
                    minSize={10}
                    id="file-explorer-panel"
                    order={2}
                  >
                    <SectionPanel title="Results">
                      <Finder />
                    </SectionPanel>
                  </ResizablePanel>
                )}
              </ResizablePanelGroup>
            </TooltipProvider>
          ) : (
            <UnauthenticatedState />
          )}
        </ResizablePanel>
        <ResizableHandle />
      </>
    );
  }
  return null;
}
